import { VisibilityOff } from '@mui/icons-material'
import { Alert, Box, Stack } from '@mui/material'
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import type { ReactNode } from 'react'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import CustomButton from '@/components/inputs/CustomButton'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import { ControllerListHeader } from '@/features/controller/components/bulkActions/ControllerListHeader'
import { ControllerListImporter } from '@/features/controller/components/bulkActions/ControllerListImporter'
import { SiteForController } from '@/features/controller/components/bulkActions/SiteForController'
import type { UseControllerListResult } from '@/features/controller/hooks/bulkActions/useControllerList'
import type { ControllerListRow } from '@/features/controller/hooks/bulkActions/useControllerListRows'
import type { SelectableController } from '@/features/controller/types'

const ACTIONS_COLUMN_WIDTH = 58

type ControllerListProps = {
  bulkActionColumnTitle: string | null
  controllerList: UseControllerListResult
  renderBulkActionColumn: (controller: SelectableController) => ReactNode
  updateInProgress: boolean
}

export function ControllerList({
  bulkActionColumnTitle,
  controllerList,
  renderBulkActionColumn,
  updateInProgress,
}: Readonly<ControllerListProps>) {
  const { t } = useTranslation()

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: 'include', ids: new Set(controllerList.selectedRawValues) }),
    [controllerList.selectedRawValues],
  )

  // Removing the invalid entries, or the last controller, also removes the button just pressed, and starts
  // the importer's text field again, so the new field takes the focus when it is mounted
  const focusImporterTextOnMount = useRef(false)

  // Stable, so React calls it only when the field is mounted or unmounted, not on every render
  const handleImporterTextFieldMounted = useCallback((textField: HTMLTextAreaElement | null) => {
    if (textField && focusImporterTextOnMount.current) {
      focusImporterTextOnMount.current = false
      textField.focus()
    }
  }, [])

  function handleRemoveInvalidEntries() {
    focusImporterTextOnMount.current = true
    controllerList.removeInvalidEntries()
  }

  function handleRemoveController(row: ControllerListRow) {
    // The grid goes away with its last row
    focusImporterTextOnMount.current = controllerList.rows.length === 1
    controllerList.removeController(row)
  }

  function handleRowSelectionModelChange(newSelection: GridRowSelectionModel) {
    controllerList.setSelectedRawValues(new Set([...newSelection.ids].map(String)))
  }

  const columns: GridColDef<ControllerListRow>[] = [
    {
      field: 'equipmentNumber',
      flex: 1,
      headerName: t('controllers.controller'),
      renderCell: ({ row }) => (
        <ControllerListHeader controllerListIdentifier={row.identifier} controllerQueryResult={row.query} />
      ),
    },
    {
      field: 'site',
      flex: 2,
      headerName: 'Site',
      renderCell: ({ row }) => <SiteForController controller={row.query.data ?? null} />,
      sortable: false,
    },
    ...(bulkActionColumnTitle
      ? [
          {
            field: 'bulkAction',
            flex: 5,
            headerName: bulkActionColumnTitle,
            renderCell: ({ row }) => {
              const controller = row.query.data
              const isSelected = controllerList.selectedRawValues.has(row.identifier.rawValue)
              return controller ? renderBulkActionColumn({ controller, isSelected }) : <Box />
            },
            sortable: false,
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: '',
      maxWidth: ACTIONS_COLUMN_WIDTH,
      minWidth: ACTIONS_COLUMN_WIDTH,
      renderCell: ({ row }) => (
        <Box>
          <CustomIconButton
            Icon={VisibilityOff}
            tooltipProps={{ title: t('controllers.bulk_actions.grid.remove') }}
            onClick={() => handleRemoveController(row)}
          />
        </Box>
      ),
      sortable: false,
    },
  ]

  return (
    <Stack gap={2}>
      <ControllerListImporter
        listText={controllerList.listText}
        textFieldRef={handleImporterTextFieldMounted}
        onUpdate={controllerList.setListText}
      />

      {controllerList.invalidIdentifiers.length > 0 && (
        <Alert
          action={
            <CustomButton color="inherit" size="small" onClick={handleRemoveInvalidEntries}>
              {t('controllers.bulk_actions.importer_remove_invalid')}
            </CustomButton>
          }
          severity="warning"
        >
          {t('controllers.bulk_actions.importer_invalid', { invalidIds: controllerList.invalidIdentifiers.join(', ') })}
        </Alert>
      )}

      {controllerList.rows.length > 0 && (
        <CustomDataGrid
          checkboxSelection={true}
          clickableRows={{
            isRowSelectable: ({ row }) => Boolean(row.query.data),
            onRowSelectionModelChange: handleRowSelectionModelChange,
          }}
          columns={columns}
          disableRowSelectionExcludeModel={true}
          disableRowSelectionOnClick={true}
          getRowId={(row: ControllerListRow) => row.identifier.rawValue}
          includeWrapper={false}
          pageSize={20}
          rowSelection={{
            model: rowSelectionModel,
            multiple: true,
          }}
          rows={controllerList.rows}
          style={updateInProgress ? { border: '2px dashed lightblue' } : { border: '2px solid white' }}
        />
      )}
    </Stack>
  )
}
